import { Skeleton } from 'primereact/skeleton';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { RecommendationReport } from './pdfGenerator';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import { selectWidgetAIAtom } from '~/atoms/dashboardAtom';
import { useSetAtom } from 'jotai';
import { NoDataAvailableTemplate } from '../../common/NoDataAvailableTemplate';
import { WidgetHeader } from '../../common/WidgetHeader';
import { useWidgetPopout } from '~/hooks/useWidgetPopout';
import { generateReportPDF } from './pdfGenerator';

export type RecommendationReportWidgetProps = {
  data: RecommendationReport[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  handleRemoveWidget?: (widgetId: string) => void;
  widgetId: string;
  title?: string;
  snapshotId?: string | null;
  portfolioName?: string;
};

// Custom styles for markdown rendering
const markdownComponents: Components = {
  h1: ({ children, ...props }) => (
    <h1
      {...props}
      className="text-xl font-bold mt-4 mb-3 first:mt-0"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2
      {...props}
      className="text-lg font-bold mt-4 mb-3 first:mt-0"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3
      {...props}
      className="text-base font-semibold mt-3 mb-2 first:mt-0"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4
      {...props}
      className="text-sm font-medium mt-2 mb-1"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h4>
  ),
  h5: ({ children, ...props }) => (
    <h5
      {...props}
      className="text-sm font-medium mt-2 mb-1"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h5>
  ),
  h6: ({ children, ...props }) => (
    <h6
      {...props}
      className="text-sm font-medium mt-2 mb-1"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </h6>
  ),
  p: ({ children, ...props }) => (
    <p
      {...props}
      className="mb-2 text-sm"
      style={{ color: 'var(--bq-text-secondary)' }}
    >
      {children as ReactNode}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong
      {...props}
      className="font-semibold"
      style={{ color: 'var(--bq-text-primary)' }}
    >
      {children as ReactNode}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em
      {...props}
      className="italic"
      style={{ color: 'var(--bq-text-secondary)' }}
    >
      {children as ReactNode}
    </em>
  ),
  ul: ({ children, ...props }) => (
    <ul
      {...props}
      className="list-disc list-inside mb-2 text-sm"
      style={{ color: 'var(--bq-text-secondary)' }}
    >
      {children as ReactNode}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      {...props}
      className="list-decimal list-inside mb-2 text-sm"
      style={{ color: 'var(--bq-text-secondary)' }}
    >
      {children as ReactNode}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li {...props} className="mb-1">
      {children as ReactNode}
    </li>
  ),
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-3">
      <table
        {...props}
        className="text-sm border-collapse"
        style={{
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--bq-border-muted)',
          minWidth: 'max-content',
        }}
      >
        {children as ReactNode}
      </table>
    </div>
  ),
  thead: ({ children, ...props }) => (
    <thead {...props} style={{ backgroundColor: 'var(--bq-surface-muted)' }}>
      {children as ReactNode}
    </thead>
  ),
  tbody: ({ children, ...props }) => (
    <tbody {...props}>{children as ReactNode}</tbody>
  ),
  tr: ({ children, ...props }) => (
    <tr {...props} style={{ borderBottom: '1px solid var(--bq-border-muted)' }}>
      {children as ReactNode}
    </tr>
  ),
  th: ({ children, ...props }) => (
    <th
      {...props}
      className="text-left font-semibold text-sm whitespace-nowrap"
      style={{
        color: 'var(--bq-text-primary)',
        borderRight: '1px solid var(--bq-border-muted)',
        padding: '0.75rem 1rem',
      }}
    >
      {children as ReactNode}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td
      {...props}
      className="text-sm"
      style={{
        color: 'var(--bq-text-secondary)',
        borderRight: '1px solid var(--bq-border-muted)',
        padding: '0.75rem 1rem',
        whiteSpace: 'nowrap',
      }}
    >
      {children as ReactNode}
    </td>
  ),
};

const ExpandableContent: React.FC<{ content: string; title: string }> = ({
  content,
  title,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldTruncate = content.length > 300;
  const displayText =
    isExpanded || !shouldTruncate ? content : content.substring(0, 300) + '...';

  return (
    <div className="mb-3">
      <strong style={{ color: 'var(--bq-text-primary)' }}>{title}:</strong>
      <div
        className="mt-1 text-sm"
        style={{ color: 'var(--bq-text-secondary)' }}
      >
        {displayText}
      </div>
      {shouldTruncate && (
        <Button
          link
          size="small"
          className="p-0 text-xs mt-1"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </div>
  );
};

export const RecommendationReportWidget: React.FC<
  RecommendationReportWidgetProps
> = ({
  data,
  isLoading,
  error,
  refetch,
  handleRemoveWidget,
  widgetId,
  title = 'AI Recommendation Report',
  snapshotId,
  portfolioName,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const widgetRef = useRef<HTMLDivElement>(null);

  const removeWidget = useCallback(
    () => handleRemoveWidget?.(widgetId),
    [widgetId, handleRemoveWidget],
  );

  // Add popout functionality
  const handlePopout = useWidgetPopout({
    widgetId,
    widgetComponent: RecommendationReportWidget,
    title: title || 'AI Recommendation Report',
    defaultWidth: 1000,
    defaultHeight: 800,
    widgetProps: {
      data,
      isLoading,
      error,
      refetch,
      handleRemoveWidget,
      widgetId,
      title,
      snapshotId,
    },
  });

  const setSelectWidgetAI = useSetAtom(selectWidgetAIAtom);

  const filteredData = useMemo(() => {
    return data.filter((report) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        report.title.toLowerCase().includes(searchLower) ||
        report.recommendations.some(
          (rec) =>
            rec.ticker.toLowerCase().includes(searchLower) ||
            rec.reasoning.toLowerCase().includes(searchLower),
        ) ||
        report.key_findings.some((finding) =>
          finding.toLowerCase().includes(searchLower),
        )
      );
    });
  }, [data, searchTerm]);

  const noData = useMemo(() => {
    return !filteredData.length;
  }, [filteredData]);

  // Export to PDF - download all visible reports using the existing PDF generation
  const handleExport = useCallback(async () => {
    if (!filteredData || filteredData.length === 0) {
      console.warn('Cannot export: no reports available');
      return;
    }

    setIsExporting(true);
    try {
      // Download all filtered reports sequentially
      for (const report of filteredData) {
        await generateReportPDF(report, portfolioName || undefined);
      }
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [filteredData, portfolioName]);

  if (isLoading) {
    return (
      <div className="p-3 h-full">
        <Skeleton className="h-full" data-testid="skeleton" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data && data.length > 0 ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="Something went wrong."
          isError
        />
      </div>
    );
  }

  if (!snapshotId) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data && data.length > 0 ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          icon="pi pi-exclamation-triangle"
          title="No Snapshot Available."
          subTitle="Run portfolio analysis to generate snapshot"
        />
      </div>
    );
  }

  if (noData && !searchTerm) {
    return (
      <div className="h-full hover-container" id={widgetId} ref={widgetRef}>
        <WidgetHeader
          title={title}
          removeWidget={removeWidget}
          refetch={() => refetch()}
          setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
          onPopout={handlePopout}
          onExport={data && data.length > 0 ? handleExport : undefined}
          isExporting={isExporting}
          widgetId={widgetId}
        />
        <NoDataAvailableTemplate
          title="No data found."
          icon="pi pi-chart-line"
        />
      </div>
    );
  }

  return (
    <div
      className="hover-container h-full flex flex-column"
      id={widgetId}
      ref={widgetRef}
    >
      <WidgetHeader
        title={title}
        removeWidget={removeWidget}
        refetch={() => refetch()}
        setSelectWidgetAI={() => setSelectWidgetAI(widgetId)}
        onPopout={handlePopout}
        onExport={data && data.length > 0 ? handleExport : undefined}
        isExporting={isExporting}
        widgetId={widgetId}
      />

      {/* Sticky Search Controls */}
      <div className="sticky top-0 z-1">
        <div className="flex flex-column gap-2 px-3 pt-3">
          <div className="p-inputgroup flex-1">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
            <InputText
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search reports by title, analysis, or key findings..."
              className="flex-1"
              size="small"
            />
            {searchTerm && (
              <span
                className="p-inputgroup-addon cursor-pointer"
                style={{
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    'var(--bq-surface-muted)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={() => setSearchTerm('')}
              >
                <i className="pi pi-times"></i>
              </span>
            )}
          </div>
          {searchTerm && (
            <div
              className="text-xs"
              style={{ color: 'var(--bq-text-tertiary)' }}
            >
              Showing {filteredData.length} of {data.length} reports
            </div>
          )}
        </div>
      </div>
      <div className="px-3">
        <Divider className="my-3" />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto flex flex-column gap-4 px-3 pb-3">
        {noData ? (
          <div
            className="p-4 text-center"
            style={{ color: 'var(--bq-text-secondary)' }}
          >
            <i className="pi pi-search text-2xl mb-2 block"></i>
            <p>No reports found matching "{searchTerm}"</p>
            <p
              className="text-sm mt-1"
              style={{ color: 'var(--bq-text-tertiary)' }}
            >
              Try adjusting your search terms
            </p>
          </div>
        ) : (
          filteredData.map((report, index) => (
            <Card
              key={`${widgetId}-${report.id}-${index}`}
              title={
                <div className="flex justify-content-between align-items-center">
                  <span className="text-lg font-semibold">{report.title}</span>
                  <Badge
                    value={new Date(report.generated_at).toLocaleDateString()}
                    severity="info"
                    className="text-xs"
                  />
                </div>
              }
              pt={{
                body: {
                  className: 'flex flex-column',
                },
                title: {
                  className: 'text-lg font-semibold mb-0',
                },
                content: {
                  className: 'p-0 m-0',
                },
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={markdownComponents}
                className="text-sm"
                style={{ color: 'var(--bq-text-secondary)' }}
              >
                {report.content}
              </ReactMarkdown>

              {report.analyst_notes && (
                <ExpandableContent
                  content={report.analyst_notes}
                  title="Analyst Notes"
                />
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
